<template>
  <div class="message-board">
    <div class="board-wrapper">
      <!-- 顶部 -->
      <div class="board-header">
        <h2 class="board-title">
          <svg class="header-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6h16M4 10h16M4 14h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          留言板
        </h2>
        <span class="board-subtitle">欢迎留言交流</span>
      </div>

      <!-- 留言输入区 -->
      <div class="message-input-card">
        <div class="input-header">
          <div class="input-avatar">
            <span class="avatar-letter">{{ getAvatarText() }}</span>
          </div>
          <span class="input-label">说点什么吧...</span>
        </div>
        <div class="mention-textarea-wrapper">
          <mention-popup
            :visible="boardMention.state.mentionVisible"
            :users="boardMention.state.filteredUsers"
            :active-index="boardMention.state.mentionActiveIdx"
            :keyword="boardMention.state.mentionKeyword"
            :loading="boardMention.state.loading"
            @select="onBoardMentionSelect"
            @update:activeIndex="boardMention.state.mentionActiveIdx = $event"
            @close="boardMention.closeMention()"
          />
          <el-input
            ref="boardTextarea"
            v-model="newMessage"
            type="textarea"
            :rows="3"
            placeholder="输入你的留言..."
            maxlength="500"
            show-word-limit
            class="input-textarea"
            @input="onBoardInput"
            @keydown.native="onBoardKeydown"
            @blur="boardMention.scheduleClose()"
          ></el-input>
        </div>
        <div class="input-footer">
          <span class="input-tip">文明发言，理性讨论</span>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">
            发表留言
          </el-button>
        </div>
      </div>

      <!-- 统计信息 -->
      <div class="board-stats">
        <span>共 <strong>{{ total }}</strong> 条留言</span>
      </div>

      <!-- 留言列表 -->
      <div v-loading="loading" class="message-list">
        <div v-for="(msg, index) in messages" :key="msg.id" class="message-card">
          <div class="msg-avatar">
            <span class="avatar-letter">{{ getAvatarTextByName(msg.trueName) }}</span>
            <span v-if="msg.role === 'admin'" class="avatar-admin-tag">管</span>
          </div>
          <div class="msg-body">
            <div class="msg-header">
              <span class="msg-username">{{ msg.trueName || '匿名用户' }}</span>
              <span class="msg-time">{{ formatTime(msg.createdAt) }}</span>
            </div>
            <p class="msg-content">{{ msg.content }}</p>
            <el-button
              v-if="canDelete(msg)"
              class="delete-btn"
              type="text"
              link
              size="small"
              @click="handleDelete(msg)"
            >
              <i class="el-icon-delete"></i>
              删除
            </el-button>
          </div>
        </div>

        <el-empty v-if="!loading && messages.length === 0" description="暂无留言，快来发表第一条留言吧"></el-empty>
      </div>

      <!-- 分页 -->
      <el-pagination
        :current-page="page"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        class="board-pagination"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { getMessages, createMessage, deleteMessage } from "@/api/screen";
import { useMention } from '@/composables/useMention'
import MentionPopup from './components/MentionPopup.vue'
import { filterSensitiveWords } from '@/utils/sensitiveWords'

export default {
  name: "ScreenMessageBoard",
  components: { MentionPopup },
  data() {
    return {
      loading: false,
      submitting: false,
      messages: [],
      newMessage: '',
      page: 1,
      pageSize: 10,
      total: 0,
      currentUser: null,
      boardMention: null
    };
  },
  created() {
    this.boardMention = useMention();
    this.initUser();
    this.fetchMessages();
  },
  methods: {
    getCursor() {
      if (!this.$refs.boardTextarea) return 0
      const el = this.$refs.boardTextarea.$el || this.$refs.boardTextarea
      const textarea = el.querySelector ? el.querySelector('textarea') : el
      return textarea ? textarea.selectionEnd : 0
    },
    setCursor(pos) {
      this.$nextTick(() => {
        const el = this.$refs.boardTextarea && (this.$refs.boardTextarea.$el || this.$refs.boardTextarea)
        const textarea = el && (el.querySelector ? el.querySelector('textarea') : el)
        if (textarea) {
          textarea.selectionStart = textarea.selectionEnd = pos
          textarea.focus()
        }
      })
    },
    onBoardInput() {
      const cursor = this.getCursor()
      this.boardMention.detectMention(this.newMessage, cursor)
    },
    onBoardKeydown(e) {
      if (!this.boardMention.state.mentionVisible) return
      if (e.key === 'ArrowDown') { e.preventDefault(); this.boardMention.moveDown() }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.boardMention.moveUp() }
      else if (e.key === 'Enter') { e.preventDefault(); this.applyBoardMention() }
      else if (e.key === 'Escape') { e.preventDefault(); this.boardMention.closeMention() }
    },
    applyBoardMention(user) {
      const result = user
        ? this.boardMention.selectUser(user)
        : this.boardMention.confirmSelection()
      if (!result) return
      const before = this.newMessage.slice(0, result.anchorPos)
      const cursorPos = this.getCursor()
      const after = this.newMessage.slice(cursorPos)
      this.newMessage = before + result.displayName + after
      this.setCursor(result.cursorAfter)
    },
    onBoardMentionSelect(user) {
      this.applyBoardMention(user)
    },
    initUser() {
      const userStr = window.sessionStorage.getItem("user");
      if (userStr) {
        try {
          this.currentUser = JSON.parse(userStr);
        } catch (e) {
          this.currentUser = null;
        }
      }
    },
    getAvatarText() {
      if (!this.currentUser) return '?';
      const name = this.currentUser.trueName || '?';
      return name.charAt(0).toUpperCase();
    },
    getAvatarTextByName(name) {
      if (!name) return '?';
      return name.charAt(0);
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const now = new Date();
      const diff = now - d;
      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前';
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前';
      if (diff < 604800000) return Math.floor(diff / 86400000) + '天前';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${mins}`;
    },
    canDelete(msg) {
      if (!this.currentUser) return false;
      const uid = window.sessionStorage.getItem("uid");
      // 游客 (guest) 无删除权限
      if (this.currentUser.userName === 'guest') return false;
      // 管理员 (uid=10) 或本人可删除
      return uid == 10 || uid == msg.userId;
    },
    fetchMessages() {
      this.loading = true;
      getMessages({
        page: this.page,
        pageSize: this.pageSize
      }).then(resp => {
        if (resp && resp.code === 200) {
          this.messages = resp.data || [];
          this.total = resp.total || 0;
        } else {
          this.$message.error(resp.msg || '获取留言失败');
        }
      }).catch(() => {
        this.$message.error('获取留言失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSubmit() {
      if (!this.newMessage.trim()) {
        this.$message.warning('请输入留言内容');
        return;
      }
      const { text, matchCount } = filterSensitiveWords(this.newMessage.trim())
      if (matchCount > 0) {
        this.$message.warning(`内容包含 ${matchCount} 个敏感词，已自动替换为*`)
        this.newMessage = text
      }
      this.submitting = true;
      createMessage({
        content: text
      }).then(resp => {
        if (resp && resp.code === 200) {
          this.$message.success('留言成功');
          this.newMessage = '';
          this.page = 1;
          this.fetchMessages();
        } else {
          this.$message.error(resp.msg || '留言失败');
        }
      }).catch(() => {
        this.$message.error('留言失败');
      }).finally(() => {
        this.submitting = false;
      });
    },
    handleDelete(msg) {
      this.$confirm('确定要删除这条留言吗？', '提示', { type: 'warning' })
        .then(() => {
          deleteMessage(msg.id)
            .then(resp => {
              if (resp && resp.code === 200) {
                this.$message.success('删除成功');
                this.fetchMessages();
              } else {
                this.$message.error(resp.msg || '删除失败');
              }
            })
            .catch(() => {
              this.$message.error('删除失败');
            });
        })
        .catch(() => {});
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.page = 1;
      this.fetchMessages();
    },
    handleCurrentChange(val) {
      this.page = val;
      this.fetchMessages();
    }
  }
};
</script>

<style scoped>
.message-board {
  min-height: 100vh;
  padding: 32px 24px;
  overflow: auto;
}

.board-wrapper {
  max-width: 900px;
  margin: 0 auto;
  overflow: auto;
}

.board-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 20px;
}

.board-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
}

.header-icon {
  width: 20px;
  height: 20px;
  color: #999;
}

.board-subtitle {
  font-size: 13px;
  color: #999;
}

.message-input-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.input-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.input-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #52c41a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-letter {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.input-label {
  font-size: 14px;
  color: #999;
}

.input-textarea {
  margin-bottom: 8px;
}

.input-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.input-tip {
  font-size: 12px;
  color: #bbb;
}

.board-stats {
  margin-bottom: 12px;
  font-size: 13px;
  color: #999;
}

.board-stats strong {
  color: #1d762a;
  margin: 0 2px;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 500px);
  overflow: auto;
  margin-bottom: 24px;
}

.message-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  gap: 12px;
}

.msg-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f5a623;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.msg-avatar .avatar-letter {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.avatar-badge {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.msg-body {
  flex: 1;
  min-width: 0;
}

.msg-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.msg-username {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.msg-time {
  font-size: 12px;
  color: #bbb;
}

.msg-index {
  font-size: 12px;
  color: #bbb;
  margin-left: auto;
}

.msg-content {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.delete-btn {
  font-size: 12px;
  padding: 0;
  height: auto;
  color: #ff4d4f;
}

.delete-btn:hover {
  color: #ff7875;
}

.mention-textarea-wrapper {
  position: relative;
  width: 100%;
}

.board-pagination {
  margin-top: 16px;
  text-align: center;
}
</style>